import React, { useCallback, useMemo, useState } from "react";
import { sortBy, groupBy } from "lodash-es";
import { Add, Bin, Edit } from "react-ikonate";
import { ReactComponent as SubcategoryIcon } from "shared/assets/SubCategory Icon.svg";
import createNotification from "shared/@utils/createNotification";
import { ascendingBy } from "shared/@utils/sort";
import Button, { ButtonSize } from "shared/Button";
import ControlsHeader from "shared/ControlsHeader";
import { Section } from "shared/layout";
import { Modal } from "shared/Modal";
import styles from "./SSF.module.css";
import SSFCategoryBanner from "../components/SSFCategoryBanner";
import { SSFCategoryEdit } from "../components/SSFCategoryEdit/SSFCategoryEdit";
import CategoryBanner from "../components/CategoryBanner";
import { CategoryEdit } from "../components/CategoryEdit/CategoryEdit";
import CategoryListItem from "../components/CategoryListItem";
import { useSSFData } from "./useSSFData";
import {
  applyCategoryMove,
  getCategoryDropPosition,
  getCategoryMove,
} from "../categoryDrag";

/**
 * Structure component. Provides category editing functionality for Simple Selling Framework.
 */
export function SSF() {
  const ssf = useSSFData();
  const [addIsOpen, setAddIsOpen] = useState(false);
  const [draggedCategoryId, setDraggedCategoryId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const published = useMemo(
    () =>
      sortBy(
        ssf.categories.filter(
          (category) =>
            category.parentCategoryID == null && category.isPublished
        ),
        "stepNumber"
      ),
    [ssf.categories]
  );

  const drafts = useMemo(
    () => ssf.categories.filter((category) => !category.isPublished),
    [ssf.categories]
  );

  const getSubcategories = useCallback(
    (categoryId) => {
      return ssf.categories.filter(
        (category) => category.parentCategoryID === categoryId
      );
    },
    [ssf.categories]
  );

  const onSubcategoryAdd = useCallback(
    (category, parentId) => {
      return ssf.addCategoryMutation.mutateAsync({
        ...category,
        parentCategoryID: parentId,
      });
    },
    [ssf.categories]
  );

  const onCategoryUpdate = useCallback(
    (category) => {
      ssf.setCategories(
        ssf.categories.map((c) => {
          if (c.id === category.id) {
            return category;
          }

          return c;
        })
      );
    },
    [ssf.categories]
  );

  const onCategoryDelete = useCallback(
    (category) => {
      ssf.setCategories(ssf.categories.filter((c) => c.id !== category.id));

      createNotification({
        type: "info",
        title: "Category Deleted",
        message: "Click Cancel or refresh the page to undo.",
        autoDismiss: true,
      });
    },
    [ssf.categories]
  );

  function getMove(sourceId, targetId, position) {
    const source = ssf.categories.find((category) => category.id === sourceId);
    return getCategoryMove(
      ssf.categories,
      sourceId,
      targetId,
      position,
      source?.parentCategoryID == null ? source?.rowNumber : undefined
    );
  }

  function onReorder(sourceId, targetId, position) {
    const move = getMove(sourceId, targetId, position);
    if (move) ssf.setCategories(applyCategoryMove(ssf.categories, move, false));
  }

  if (ssf.isLoading) {
    return "Loading...";
  }

  if (ssf.isError) {
    console.error(ssf.error);
    return ssf.error.message;
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.header}>
        <ControlsHeader title="Simple Selling Framework">
          <Button
            icon={<Add />}
            disabled={addIsOpen}
            onClick={() => {
              setAddIsOpen(true);
            }}
          >
            New Category
          </Button>

          <Button
            disabled={!ssf.isChanged}
            onClick={() => {
              ssf.saveMutation.mutate();
            }}
            purpose="save"
          >
            <p>Save</p>
          </Button>

          <Button
            disabled={!ssf.isChanged}
            onClick={() => {
              ssf.reset();
            }}
          >
            Cancel
          </Button>
        </ControlsHeader>
      </div>

      {addIsOpen && (
        <SSFCategoryEdit
          showPreview={true}
          onSubmit={async (category) => {
            return ssf.addCategoryMutation.mutateAsync(category).then(() => {
              setAddIsOpen(false);
            });
          }}
          onCancel={() => {
            setAddIsOpen(false);
          }}
        />
      )}

      <Section>
        <header>
          <h3>Published</h3>
        </header>
        <SSFCategoryList
          categories={published}
          getMove={getMove}
          draggedCategoryId={draggedCategoryId}
          setDraggedCategoryId={setDraggedCategoryId}
          dropTarget={dropTarget}
          setDropTarget={setDropTarget}
          onReorder={onReorder}
          getSubcategories={getSubcategories}
          onSubcategoryAdd={onSubcategoryAdd}
          onCategoryUpdate={onCategoryUpdate}
          onCategoryDelete={onCategoryDelete}
        />
      </Section>

      <Section>
        <header>
          <h3>Drafts</h3>
        </header>
        <SSFCategoryList
          categories={drafts}
          getMove={getMove}
          draggedCategoryId={draggedCategoryId}
          setDraggedCategoryId={setDraggedCategoryId}
          dropTarget={dropTarget}
          setDropTarget={setDropTarget}
          onReorder={onReorder}
          getSubcategories={getSubcategories}
          onSubcategoryAdd={onSubcategoryAdd}
          onCategoryUpdate={onCategoryUpdate}
          onCategoryDelete={onCategoryDelete}
        />
      </Section>
    </div>
  );
}

const EDIT_TYPE = {
  SSF: "SSF",
  subcategory: "sub",
};

function SSFCategoryList({
  categories,
  isSubcategoryList = false,
  getMove,
  draggedCategoryId,
  setDraggedCategoryId,
  dropTarget,
  setDropTarget,
  onReorder,
  getSubcategories,
  onSubcategoryAdd,
  onCategoryUpdate,
  onCategoryDelete,
}) {
  const [editCategory, setEditCategory] = useState(null);
  const [editCategoryType, setEditCategoryType] = useState(EDIT_TYPE.SSF);

  const [isAddingSubcategory, setIsAddingSubcategory] = useState(false);
  const [subcategoryParent, setSubcategoryParent] = useState(null);

  function clearDrag() {
    setDraggedCategoryId(null);
    setDropTarget(null);
  }

  function getReorderProps(category, siblings) {
    if (!onReorder || !category.isPublished) return {};

    const getPosition = (event) =>
      getCategoryDropPosition(
        event,
        !!getMove(draggedCategoryId, category.id, "inside")
      );

    return {
      isDragging: draggedCategoryId === category.id,
      dropPosition: dropTarget?.id === category.id ? dropTarget.position : null,
      onReorderKeyDown: (event) => {
        if (!["ArrowUp", "ArrowDown"].includes(event.key)) return;
        event.preventDefault();
        const direction = event.key === "ArrowUp" ? -1 : 1;
        const neighbor =
          siblings[
            siblings.findIndex((item) => item.id === category.id) + direction
          ];
        if (neighbor)
          onReorder(
            category.id,
            neighbor.id,
            direction < 0 ? "above" : "below"
          );
      },
      dragProps: {
        draggable: true,
        "data-category-id": category.id,
        onDragStart: (event) => {
          event.stopPropagation();
          if (event.target.closest("button, input, form")) {
            event.preventDefault();
            return;
          }
          event.dataTransfer.effectAllowed = "move";
          event.dataTransfer.setData("text/plain", String(category.id));
          setDraggedCategoryId(category.id);
        },
        onDragOver: (event) => {
          event.stopPropagation();
          const position = getPosition(event);
          if (!getMove(draggedCategoryId, category.id, position)) {
            setDropTarget(null);
            return;
          }
          event.preventDefault();
          event.dataTransfer.dropEffect = "move";
          setDropTarget({ id: category.id, position });
        },
        onDrop: (event) => {
          event.stopPropagation();
          event.preventDefault();
          const position =
            dropTarget?.id === category.id
              ? dropTarget.position
              : getCategoryDropPosition(event, false);
          if (getMove(draggedCategoryId, category.id, position)) {
            onReorder(draggedCategoryId, category.id, position);
          }
          clearDrag();
        },
        onDragEnd: (event) => {
          event.stopPropagation();
          clearDrag();
        },
        onDragLeave: (event) => {
          event.stopPropagation();
          if (
            event.relatedTarget instanceof Node &&
            event.currentTarget.contains(event.relatedTarget)
          )
            return;
          setDropTarget((current) =>
            current?.id === category.id ? null : current
          );
        },
      },
    };
  }

  function renderNestedList(parentId) {
    return (
      <SSFCategoryList
        categories={sortBy(
          getSubcategories(parentId),
          "rowNumber",
          "stepNumber"
        )}
        isSubcategoryList={true}
        getMove={getMove}
        draggedCategoryId={draggedCategoryId}
        setDraggedCategoryId={setDraggedCategoryId}
        dropTarget={dropTarget}
        setDropTarget={setDropTarget}
        onReorder={onReorder}
        getSubcategories={getSubcategories}
        onSubcategoryAdd={onSubcategoryAdd}
        onCategoryUpdate={onCategoryUpdate}
        onCategoryDelete={onCategoryDelete}
      />
    );
  }

  return (
    <>
      <ul className={styles.categoryList}>
        {categories.map((category, index) => {
          const subcategoriesCount = getSubcategories(category.id).length;

          return (
            <CategoryListItem
              key={category.id}
              {...getReorderProps(
                category,
                categories.filter((item) => item.isPublished)
              )}
              subcategoriesCount={subcategoriesCount}
              content={
                <div className={styles.categoryBanner}>
                  {isSubcategoryList ? (
                    <CategoryBanner
                      title={category.title}
                      fillColor={
                        category.colorFill ? category.backgroundColor : null
                      }
                      textColor={category.textColor}
                    />
                  ) : (
                    <SSFCategoryBanner
                      iconSrc={category.iconPath}
                      pendingIconFile={category._pendingIconUpload}
                      title={category.title}
                      subtitle={category.subTitle}
                      fillColor={category.backgroundColor}
                      textColor={category.textColor}
                      stepNumber={category.stepNumber}
                    />
                  )}
                </div>
              }
              controls={
                <>
                  <Button
                    icon={<Edit />}
                    size={ButtonSize.small}
                    onClick={() => {
                      setEditCategory(category);
                      setEditCategoryType(
                        isSubcategoryList
                          ? EDIT_TYPE.subcategory
                          : EDIT_TYPE.SSF
                      );
                    }}
                  >
                    Edit
                  </Button>
                  {!isSubcategoryList && (
                    <Button
                      icon={<SubcategoryIcon aria-hidden="true" />}
                      size={ButtonSize.small}
                      onClick={() => {
                        setIsAddingSubcategory(true);
                        setSubcategoryParent(category.id);
                      }}
                    >
                      Add Subcategory
                    </Button>
                  )}
                  <Button
                    icon={<Bin />}
                    size={ButtonSize.small}
                    disabled={subcategoriesCount > 0}
                    onClick={() => {
                      onCategoryDelete(category);
                    }}
                  >
                    Delete Category
                  </Button>
                </>
              }
              renderSubcategories={() => {
                if (isSubcategoryList) return renderNestedList(category.id);
                const subcategories = getSubcategories(category.id);

                const published = subcategories.filter((c) => c.isPublished);
                const drafts = subcategories.filter((c) => !c.isPublished);

                const byRow = groupBy(published, (x) => x.rowNumber || 1);
                const groups = Object.keys(byRow)
                  .sort()
                  .map((row) => ({
                    name: `Row ${row}`,
                    categories: byRow[row].sort(ascendingBy("stepNumber")),
                  }));
                const inGroupedOrder = groups.reduce(
                  (arr, group) => [...arr, ...group.categories],
                  []
                );

                return (
                  <>
                    {groups.map((group) => {
                      return (
                        <div key={group.name}>
                          <h4 className={styles.groupHeader}>{group.name}</h4>
                          <ul>
                            {group.categories.map((subcategory) => (
                              <CategoryListItem
                                key={subcategory.id}
                                subcategoriesCount={
                                  getSubcategories(subcategory.id).length
                                }
                                renderSubcategories={() =>
                                  renderNestedList(subcategory.id)
                                }
                                {...getReorderProps(
                                  subcategory,
                                  inGroupedOrder
                                )}
                                content={
                                  <div className={styles.categoryBanner}>
                                    <CategoryBanner
                                      title={subcategory.title}
                                      fillColor={
                                        subcategory.colorFill
                                          ? subcategory.backgroundColor
                                          : null
                                      }
                                      textColor={subcategory.textColor}
                                    />
                                  </div>
                                }
                                controls={
                                  <>
                                    <Button
                                      icon={<Edit />}
                                      size={ButtonSize.small}
                                      onClick={() => {
                                        setEditCategory(subcategory);
                                        setEditCategoryType(
                                          EDIT_TYPE.subcategory
                                        );
                                      }}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      icon={<Bin />}
                                      size={ButtonSize.small}
                                      disabled={
                                        getSubcategories(subcategory.id)
                                          .length > 0
                                      }
                                      onClick={() => {
                                        onCategoryDelete(subcategory);
                                      }}
                                    >
                                      Delete Category
                                    </Button>
                                  </>
                                }
                              />
                            ))}
                          </ul>
                        </div>
                      );
                    })}

                    {drafts.length > 0 && (
                      <div>
                        <h4 className={styles.groupHeader}>Drafts</h4>
                        <ul>
                          {drafts.map((subcategory) => (
                            <CategoryListItem
                              key={subcategory.id}
                              // isFirst and isLast are true to disable ordering for drafts
                              isFirst={true}
                              isLast={true}
                              content={
                                <div className={styles.categoryBanner}>
                                  <CategoryBanner
                                    title={subcategory.title}
                                    fillColor={
                                      subcategory.colorFill
                                        ? subcategory.backgroundColor
                                        : null
                                    }
                                    textColor={subcategory.textColor}
                                  />
                                </div>
                              }
                              controls={
                                <>
                                  <Button
                                    icon={<Edit />}
                                    size={ButtonSize.small}
                                    onClick={() => {
                                      setEditCategory(subcategory);
                                      setEditCategoryType(
                                        EDIT_TYPE.subcategory
                                      );
                                    }}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    icon={<Bin />}
                                    size={ButtonSize.small}
                                    onClick={() => {
                                      onCategoryDelete(subcategory);
                                    }}
                                  >
                                    Delete Category
                                  </Button>
                                </>
                              }
                            />
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                );
              }}
            />
          );
        })}
      </ul>

      <Modal
        title="Add Subcategory"
        isOpen={isAddingSubcategory}
        onClose={() => {
          setIsAddingSubcategory(false);
        }}
      >
        {isAddingSubcategory && (
          <CategoryEdit
            showPreview={true}
            onSubmit={(category) => {
              onSubcategoryAdd(category, subcategoryParent);
              setIsAddingSubcategory(false);
            }}
            onCancel={() => {
              setIsAddingSubcategory(false);
            }}
          />
        )}
      </Modal>

      <Modal
        title="Edit Category"
        isOpen={Boolean(editCategory)}
        onClose={() => {
          setEditCategory(null);
        }}
      >
        {editCategory && editCategoryType === "SSF" && (
          <SSFCategoryEdit
            category={editCategory}
            showPreview={true}
            onSubmit={(updated) => {
              onCategoryUpdate(updated);
              setEditCategory(null);
            }}
            onCancel={() => {
              setEditCategory(null);
            }}
          />
        )}

        {editCategory && editCategoryType === "sub" && (
          <CategoryEdit
            category={editCategory}
            showPreview={true}
            onSubmit={(updated) => {
              onCategoryUpdate(updated);
              setEditCategory(null);
            }}
            onCancel={() => {
              setEditCategory(null);
            }}
          />
        )}
      </Modal>
    </>
  );
}
